import { EntityType, LeaderboardMetric } from "@/services/bedrock";

interface ExportOption {
  label: string;
  value: EntityType;
  metric: LeaderboardMetric;
  id: string; // Unique ID for checkbox and selection state
  description: string;
}

export interface ReportGroup {
  title: string;
  options: ExportOption[];
}

export const REPORT_GROUPS: ReportGroup[] = [
  {
    title: "People & Organization",
    options: [
      {
        id: "player_perf",
        label: "Players: Performance",
        value: "player",
        metric: "total_reactions",
        description: "Engagement summary (likes, comments, shares) for individual players.",
      },
      {
        id: "player_activity",
        label: "Players: Posting Activity",
        value: "player",
        metric: "post_count",
        description: "Number of social media posts published by each player.",
      },
      {
        id: "player_audience",
        label: "Players: Audience Growth",
        value: "player",
        metric: "follower_change_percent",
        description: "Percentage change in social media follower counts.",
      },
      {
        id: "region_perf",
        label: "Regions: Performance",
        value: "region",
        metric: "total_reactions",
        description: "Aggregated engagement metrics grouped by region.",
      },
      {
        id: "region_activity",
        label: "Regions: Posting Activity",
        value: "region",
        metric: "post_count",
        description: "Total number of social posts published within each region.",
      },
      {
        id: "region_audience",
        label: "Regions: Audience Growth",
        value: "region",
        metric: "follower_change_percent",
        description: "Average follower growth trends across regional teams.",
      },
      {
        id: "branch_perf",
        label: "Branches: Performance",
        value: "branch",
        metric: "total_reactions",
        description: "Aggregated engagement metrics grouped by each branch.",
      },
      {
        id: "branch_activity",
        label: "Branches: Posting Activity",
        value: "branch",
        metric: "post_count",
        description: "Total volume of social posts published by each branch.",
      },
      {
        id: "branch_audience",
        label: "Branches: Audience Growth",
        value: "branch",
        metric: "follower_change_percent",
        description: "Average follower growth trends across each branch.",
      },
    ],
  },
  {
    title: "Content & Campaigns",
    options: [
      {
        id: "posts",
        label: "Individual Posts",
        value: "post",
        metric: "total_reactions",
        description: "Top performing individual social media posts and their detailed stats.",
      },
      {
        id: "campaign_perf",
        label: "Campaigns: Performance",
        value: "campaign",
        metric: "total_reactions",
        description: "Success metrics for marketing campaigns.",
      },
      {
        id: "campaign_usage",
        label: "Campaigns: Engagement",
        value: "campaign",
        metric: "post_count",
        description: "Usage statistics showing how often players published campaign content.",
      },
      {
        id: "prompt_perf",
        label: "Prompts: Performance",
        value: "prompt",
        metric: "total_reactions",
        description: "Performance evaluation of individual content prompts.",
      },
      {
        id: "prompt_usage",
        label: "Prompts: Usage",
        value: "prompt",
        metric: "post_count",
        description: "Tracking the number of times players used content prompts.",
      },
      {
        id: "video_script_perf",
        label: "Video Scripts: Performance",
        value: "video_script",
        metric: "total_reactions",
        description: "Engagement metrics for posts that used video scripts.",
      },
      {
        id: "video_script_usage",
        label: "Video Scripts: Usage",
        value: "video_script",
        metric: "post_count",
        description: "Number of posts that used video scripts.",
      },
    ],
  },
  {
    title: "Platforms & Pages",
    options: [
      {
        id: "social_audience",
        label: "Socials: Audience Growth",
        value: "social_identity",
        metric: "follower_change_percent",
        description: "Growth trends grouped by connected social network accounts.",
      },
      {
        id: "social_perf",
        label: "Socials: Performance",
        value: "social_identity",
        metric: "total_reactions",
        description: "Engagement metrics grouped by connected social network accounts.",
      },
      {
        id: "social_activity",
        label: "Socials: Posting Activity",
        value: "social_identity",
        metric: "post_count",
        description: "Number of posts published by each connected social network account.",
      },
      {
        id: "video_pages",
        label: "Video Pages: Total Views",
        value: "content_page",
        metric: "view_count",
        description: "Total traffic and views for SocialCoach hosted video pages.",
      },
    ],
  },
];

export const ALL_REPORT_OPTIONS = REPORT_GROUPS.flatMap(g => g.options);

export const LEADERBOARD_HEADER_MAP: Record<string, string> = {
  impressionCount: "Impressions",
  reachCount: "Reach",
  viewCount: "Views",
  clickCount: "Clicks",
  likeCount: "Likes",
  commentCount: "Comments",
  shareCount: "Shares",
  totalReactions: "Total Reactions",
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email Address",
  name: "Name",
  title: "Title",
  username: "Username",
  internalIdentifier: "Internal ID",
  displayName: "Display Name",
  postedCaption: "Post Caption",
  postedTimestamp: "Date Posted",
  externalPostUrl: "External Post Link",
  socialNetworkId: "Social Network",
  playerLink: "SocialCoach Player Link",
  promptLink: "SocialCoach Prompt Link",
  campaignLink: "SocialCoach Campaign Link",
};

export const PRESET_DATE_OPTIONS = [
  { label: "Custom Range", value: "custom" },
  { label: "This Month so far", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "Two Months ago", value: "two_months_ago" },
  { label: "Year to Date", value: "ytd" },
  { label: "All Time", value: "all_time" },
];
